import React, { useMemo } from "react";
import { Table } from "react-bootstrap";
import {
	applyDiscount,
	getBillTaxBreakdownRows,
	toMoneyNumber,
} from "../../../../utils/financeEngine";
import { formatAmount } from "../../../PatientAccounts/utils/patientAccountUtils";
import {
	buildPosPharmacyLineAmounts,
	buildPriorPharmacyTaxInfo,
	getBillingDiscountSummaryLabel,
	getPriorPharmacyBillDiscountRate,
	resolveConfiguredPharmacyBillDiscountRate,
} from "../utils/billingUtils";

/** Same bill-discount rate used by POS CartTable (cart taxable only). */
const getPosBillDiscountRate = (cart = [], itemTaxes = {}, totals = {}) => {
	const totalTaxableBeforeDiscount = cart.reduce((sum, item) => {
		const itemKey = `${item._id}-${item.selectedBatchId}`;
		const taxInfo = itemTaxes[itemKey];
		return sum + (taxInfo?.taxableAmount || 0);
	}, 0);
	const billDiscount = totals?.discount || 0;
	return totalTaxableBeforeDiscount > 0 ? billDiscount / totalTaxableBeforeDiscount : 0;
};

const emptyPharmacyFooter = () => ({
	discount: 0,
	taxable: 0,
	taxableAfter: 0,
	mainAmount: 0,
	CGST: 0,
	SGST: 0,
	IGST: 0,
	VAT: 0,
	tax: 0,
	gross: 0,
});

const accumulatePharmacyFooter = (footer, row) => {
	footer.discount += row.discountAmount || 0;
	footer.taxable += row.taxableValue || 0;
	footer.taxableAfter += row.taxableAfterDiscount || 0;
	footer.mainAmount += row.mainAmount || 0;
	footer.CGST += row.rowCGST || 0;
	footer.SGST += row.rowSGST || 0;
	footer.IGST += row.rowIGST || 0;
	footer.VAT += row.rowVAT || 0;
	footer.tax += row.rowTax || 0;
	footer.gross += row.grossTotal || 0;
	return footer;
};

const usePharmacyRowModel = ({
	cart = [],
	itemTaxes = {},
	totals = {},
	showPosTax = true,
}) => {
	const billDiscountRate = useMemo(
		() => getPosBillDiscountRate(cart, itemTaxes, totals),
		[cart, itemTaxes, totals]
	);

	const rows = useMemo(
		() =>
			cart.map((item) => {
				const itemKey = `${item._id}-${item.selectedBatchId}`;
				const taxInfo = itemTaxes[itemKey] || {};
				const amounts = buildPosPharmacyLineAmounts({
					taxInfo,
					billDiscountRate,
					salePrice: item.salePrice || 0,
					quantity: item.quantity || 0,
					showPosTax,
				});

				return {
					itemKey,
					name: item.brandName || item.saltName,
					qty: item.quantity || 0,
					mrp: item.salePrice || 0,
					mainAmount: toMoneyNumber((item.salePrice || 0) * (item.quantity || 0)),
					discountAmount: amounts.discountAmount,
					taxRate: amounts.taxRate,
					taxableValue: amounts.taxableBeforeDiscount,
					taxableAfterDiscount: amounts.taxableAfterDiscount,
					rowCGST: amounts.rowCGST,
					rowSGST: amounts.rowSGST,
					rowIGST: amounts.rowIGST,
					rowVAT: amounts.rowVAT,
					rowTax: amounts.rowTax,
					grossTotal: amounts.grossTotal,
				};
			}),
		[billDiscountRate, cart, itemTaxes, showPosTax]
	);

	return { rows, billDiscountRate };
};

/**
 * Medicines table + summary share this model.
 * Total = sum of Taxable Amount (prior + cart, before discount).
 * Discount = bill discount calculated from that taxable Total.
 * Discount = bill discount calculated from that taxable Total.
 * Prior-only bills with a configured discount use bill-level rate on the total;
 * mixed prior+cart keeps historical prior lines and POS rate on cart.
 */
export const usePharmacyMedicinesDisplayModel = ({
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	cartTotals = {},
	discountType = "Percentage",
	discountValue = 0,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	showPosTax = true,
}) => {
	const { rows } = usePharmacyRowModel({
		cart,
		itemTaxes,
		totals: cartTotals,
		showPosTax,
	});

	const priorRowInputs = useMemo(
		() =>
			(priorPharmacyItems || []).map((item, index) => ({
				item,
				index,
				taxInfo: buildPriorPharmacyTaxInfo(
					item,
					pharmacyStateCode,
					patientStateCode,
					posTaxType
				),
			})),
		[priorPharmacyItems, pharmacyStateCode, patientStateCode, posTaxType]
	);

	const priorTaxableBeforeDiscount = useMemo(
		() =>
			priorRowInputs.reduce(
				(sum, { taxInfo }) => sum + toMoneyNumber(taxInfo.taxableAmount || 0),
				0
			),
		[priorRowInputs]
	);

	const configuredPriorBillDiscountRate = useMemo(() => {
		if (cart.length > 0) return null;
		return resolveConfiguredPharmacyBillDiscountRate({
			totalTaxableBeforeDiscount: priorTaxableBeforeDiscount,
			discountType,
			discountValue,
			cartDiscount: cartTotals?.discount,
		});
	}, [
		cart.length,
		cartTotals?.discount,
		discountType,
		discountValue,
		priorTaxableBeforeDiscount,
	]);

	const priorRows = useMemo(
		() =>
			priorRowInputs.map(({ item, index, taxInfo }) => {
				const billDiscountRate =
					configuredPriorBillDiscountRate != null
						? configuredPriorBillDiscountRate
						: getPriorPharmacyBillDiscountRate(item, taxInfo);
				const amounts = buildPosPharmacyLineAmounts({
					taxInfo,
					billDiscountRate,
					salePrice: item.unit_price || 0,
					quantity: item.qty || 0,
					showPosTax,
				});

				return {
					itemKey: `prior-${item.item_id || index}-${item.stock_id || index}`,
					name: item.item || item.brand_name || item.medicine_name || "-",
					qty: item.qty || 0,
					mrp: item.unit_price || 0,
					mainAmount: toMoneyNumber((item.unit_price || 0) * (item.qty || 0)),
					discountAmount: amounts.discountAmount,
					taxRate: amounts.taxRate,
					taxableValue: amounts.taxableBeforeDiscount,
					taxableAfterDiscount: amounts.taxableAfterDiscount,
					rowVAT: amounts.rowVAT,
					rowTax: amounts.rowTax,
					rowCGST: amounts.rowCGST,
					rowSGST: amounts.rowSGST,
					rowIGST: amounts.rowIGST,
					grossTotal: amounts.grossTotal,
					date: item.date || "",
					isPrior: true,
				};
			}),
		[
			configuredPriorBillDiscountRate,
			priorRowInputs,
			showPosTax,
		]
	);

	const combinedFooter = useMemo(() => {
		const footer = emptyPharmacyFooter();
		[...priorRows, ...rows].forEach((row) => accumulatePharmacyFooter(footer, row));
		const taxable = toMoneyNumber(footer.taxable);
		const lineDiscount = toMoneyNumber(footer.discount);
		const discountFromTotal = toMoneyNumber(
			applyDiscount(taxable, discountType, discountValue)
		);
		const useBillLevelDiscount =
			rows.length > 0 ||
			configuredPriorBillDiscountRate != null ||
			toMoneyNumber(discountValue) > 0 ||
			toMoneyNumber(cartTotals?.discount) > 0;
		const discount = useBillLevelDiscount
			? discountFromTotal > 0
				? discountFromTotal
				: toMoneyNumber(cartTotals?.discount || lineDiscount)
			: lineDiscount > 0
				? lineDiscount
				: discountFromTotal;
		return {
			discount,
			taxable,
			taxableAfter: toMoneyNumber(Math.max(0, taxable - discount)),
			mainAmount: toMoneyNumber(footer.mainAmount),
			CGST: toMoneyNumber(footer.CGST),
			SGST: toMoneyNumber(footer.SGST),
			IGST: toMoneyNumber(footer.IGST),
			VAT: toMoneyNumber(footer.VAT),
			tax: toMoneyNumber(footer.tax),
			gross: toMoneyNumber(footer.gross),
		};
	}, [
		cartTotals?.discount,
		configuredPriorBillDiscountRate,
		discountType,
		discountValue,
		priorRows,
		rows,
	]);

	return { priorRows, cartRows: rows, combinedFooter };
};

const useDisplayAmount = (displayCurrency, pharmacyCurrency, convertCurrency) =>
	useMemo(
		() => (amount) => {
			const value = toMoneyNumber(amount);
			if (displayCurrency === pharmacyCurrency) return value;
			return toMoneyNumber(convertCurrency(value, pharmacyCurrency, displayCurrency));
		},
		[convertCurrency, displayCurrency, pharmacyCurrency]
	);

export const BillingPharmacyMedicinesTable = ({
	t,
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	totals = {},
	discountType = "Percentage",
	discountValue = 0,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	showPosTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;

	const { priorRows, cartRows, combinedFooter } = usePharmacyMedicinesDisplayModel({
		cart,
		priorPharmacyItems,
		itemTaxes,
		cartTotals: totals,
		discountType,
		discountValue,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		showPosTax,
	});

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(toDisplay(amount), displayCurrency, getCurrencySymbol);

	const taxSplitColCount =
		showPosTax && posTaxType === "gst" ? (isSameState ? 2 : 1) : showPosTax ? 1 : 0;
	// Item + Qty + MRP + (Tax%) + tax-split cols + Taxable Amount
	const labelColSpan = 3 + (showPosTax ? 1 : 0) + taxSplitColCount;
	const emptyColSpan = labelColSpan + 1;
	const hasRows = priorRows.length > 0 || cart.length > 0;

	if (!hasRows) {
		return (
			<div className="table-responsive">
				<Table bordered size="sm" className="mb-0 billing-table medicines-table billing-pharmacy-table">
					<tbody>
						<tr>
							<td colSpan={emptyColSpan} className="text-center text-muted py-3">
								{t("No medicines in cart")}
							</td>
						</tr>
					</tbody>
				</Table>
			</div>
		);
	}

	const renderDataRows = (list) =>
		list.map((row) => (
			<tr
				key={row.itemKey}
				className={row.isPrior ? "billing-row billing-row--locked" : undefined}
			>
				<td>
					<div className="billing-line-name">
						<span className="billing-line-title">{row.name}</span>
						{row.isPrior && (
							<span className="billing-source-badge billing-source-badge--prescribed ms-1">
								{row.date || t("Previous")}
							</span>
						)}
					</div>
				</td>
				<td className="text-center">{row.qty}</td>
				<td className="text-end">{money(row.mrp)}</td>
				{showPosTax && (
					<>
						<td className="text-center">{row.taxRate}%</td>
						{posTaxType === "gst" ? (
							isSameState ? (
								<>
									<td className="text-end">{money(row.rowCGST)}</td>
									<td className="text-end">{money(row.rowSGST)}</td>
								</>
							) : (
								<td className="text-end">{money(row.rowIGST)}</td>
							)
						) : (
							<td className="text-end">{money(row.rowVAT || row.rowTax)}</td>
						)}
					</>
				)}
				<td className="text-end fw-semibold">{money(row.taxableValue)}</td>
			</tr>
		));

	return (
		<div className="table-responsive">
			<Table bordered size="sm" className="mb-0 billing-table medicines-table billing-pharmacy-table">
				<thead>
					<tr>
						<th scope="col">{t("Item")}</th>
						<th scope="col" className="text-center billing-col-qty">
							{t("Qty")}
						</th>
						<th scope="col" className="text-center billing-col-mrp">
							{t("Price (MRP)")}
						</th>
						{showPosTax && (
							<>
								<th scope="col" className="text-center billing-col-tax-pct">
									{t("Tax %")}
								</th>
								{posTaxType === "gst" ? (
									isSameState ? (
										<>
											<th scope="col" className="text-center">{t("CGST")}</th>
											<th scope="col" className="text-center">{t("SGST")}</th>
										</>
									) : (
										<th scope="col" className="text-center">{t("IGST")}</th>
									)
								) : (
									<th scope="col" className="text-center">{t("VAT")}</th>
								)}
							</>
						)}
						<th scope="col" className="text-end">
							{t("Taxable Amount")}
						</th>
					</tr>
				</thead>
				<tbody>
					{renderDataRows(priorRows)}
					{renderDataRows(cartRows)}
				</tbody>
				{(priorRows.length > 0 || cartRows.length > 0) && (
					<tfoot>
						<tr>
							<td colSpan={labelColSpan} className="text-end fw-semibold">
								{t("Total")}
							</td>
							<td className="text-end fw-semibold">{money(combinedFooter.taxable)}</td>
						</tr>
						{combinedFooter.discount > 0 && (
							<tr className="billing-pharmacy-footer-discount">
								<td colSpan={labelColSpan} className="text-end">
									{t("Discount")}
								</td>
								<td className="text-end text-danger">
									-{money(combinedFooter.discount)}
								</td>
							</tr>
						)}
						{showPosTax && combinedFooter.tax > 0 && (
							<tr className="billing-pharmacy-footer-tax">
								<td colSpan={labelColSpan} className="text-end">
									{t("Tax")}
									{posTaxType === "gst"
										? isSameState
											? ` (${t("CGST")} + ${t("SGST")})`
											: ` (${t("IGST")})`
										: ` (${t("VAT")})`}
								</td>
								<td className="text-end text-success">
									+{money(combinedFooter.tax)}
								</td>
							</tr>
						)}
						<tr className="billing-pharmacy-footer-net">
							<td colSpan={labelColSpan} className="text-end fw-bold">
								{t("Pharmacy Grand Total")}
							</td>
							<td className="text-end fw-bold">
								{money(
									toMoneyNumber(
										combinedFooter.taxable - combinedFooter.discount + combinedFooter.tax
									)
								)}
							</td>
						</tr>
					</tfoot>
				)}
			</Table>
		</div>
	);
};

export const BillingPharmacyTaxBreakdown = ({
	t,
	cart = [],
	itemTaxes = {},
	totals = {},
	discountType = "Percentage",
	discountValue = 0,
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	showPosTax = true,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;
	const isGst = String(posTaxType || "").toLowerCase() === "gst";

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(toDisplay(amount), displayCurrency, getCurrencySymbol);

	const taxBreakdown = useMemo(() => {
		if (!cart.length || !itemTaxes) return [];
		return getBillTaxBreakdownRows(
			cart,
			itemTaxes,
			totals,
			posTaxType,
			isSameState,
			"sale",
			discountType,
			discountValue
		);
	}, [cart, discountType, discountValue, itemTaxes, totals, posTaxType, isSameState]);

	const hasTaxRatesInCart =
		showPosTax && cart.some((item) => Number(item.taxPercentage) > 0);
	const totalTax = toDisplay(totals?.tax || 0);
	const hasTax =
		showPosTax &&
		cart.length > 0 &&
		(totalTax > 0 || hasTaxRatesInCart) &&
		taxBreakdown.length > 0;

	const footer = useMemo(
		() =>
			taxBreakdown.reduce(
				(acc, group) => ({
					taxableBefore:
						acc.taxableBefore + toMoneyNumber(group.taxableAmountBeforeDiscount),
					taxableAfter: acc.taxableAfter + toMoneyNumber(group.netTaxableAmount),
					CGST: acc.CGST + toMoneyNumber(group.CGST),
					SGST: acc.SGST + toMoneyNumber(group.SGST),
					IGST: acc.IGST + toMoneyNumber(group.IGST),
					VAT: acc.VAT + toMoneyNumber(group.VAT),
					totalTax: acc.totalTax + toMoneyNumber(group.totalTax),
					totalAmount: acc.totalAmount + toMoneyNumber(group.totalAmount),
				}),
				{
					taxableBefore: 0,
					taxableAfter: 0,
					CGST: 0,
					SGST: 0,
					IGST: 0,
					VAT: 0,
					totalTax: 0,
					totalAmount: 0,
				}
			),
		[taxBreakdown]
	);

	if (!hasTax) return null;

	const renderTaxAmountCells = (group, { bold = false } = {}) => {
		const cellClass = `text-end${bold ? " fw-bold" : ""}`;
		if (isGst) {
			if (isSameState) {
				return (
					<>
						<td className={`${cellClass} text-success`}>{money(group.CGST)}</td>
						<td className={`${cellClass} text-success`}>{money(group.SGST)}</td>
					</>
				);
			}
			return <td className={`${cellClass} text-success`}>{money(group.IGST)}</td>;
		}
		return <td className={`${cellClass} text-success`}>{money(group.VAT)}</td>;
	};

	return (
		<div className="billing-pharmacy-tax-table-wrap">
			<div className="billing-pharmacy-tax-table-title">{t("Tax Summary")}</div>
			<div className="table-responsive">
				<Table bordered size="sm" className="mb-0 billing-table billing-pharmacy-tax-table">
					<thead>
						<tr>
							<th className="text-center">{t("Tax %")}</th>
							<th className="text-end">{t("Taxable value")}</th>
							{isGst ? (
								isSameState ? (
									<>
										<th className="text-end">{t("CGST Amt")}</th>
										<th className="text-end">{t("SGST Amt")}</th>
									</>
								) : (
									<th className="text-end">{t("IGST Amt")}</th>
								)
							) : (
								<th className="text-end">{t("VAT Amt")}</th>
							)}
							<th className="text-end">{t("Total Tax")}</th>
							<th className="text-end">{t("Total Amount")}</th>
						</tr>
					</thead>
					<tbody>
						{taxBreakdown.map((group, index) => (
							<tr key={`${group.taxRate}-${index}`}>
								<td className="text-center fw-bold">{group.taxRate}%</td>
								<td className="text-end">
									{money(group.taxableAmountBeforeDiscount)}
								</td>
								{renderTaxAmountCells(group)}
								<td className="text-end fw-bold text-success">
									{money(group.totalTax)}
								</td>
								<td className="text-end fw-bold">{money(group.totalAmount)}</td>
							</tr>
						))}
						<tr className="billing-pharmacy-tax-table__total">
							<td className="text-center fw-bold">{t("Total")}</td>
							<td className="text-end fw-bold">{money(footer.taxableBefore)}</td>
							{isGst ? (
								isSameState ? (
									<>
										<td className="text-end fw-bold">{money(footer.CGST)}</td>
										<td className="text-end fw-bold">{money(footer.SGST)}</td>
									</>
								) : (
									<td className="text-end fw-bold">{money(footer.IGST)}</td>
								)
							) : (
								<td className="text-end fw-bold">{money(footer.VAT)}</td>
							)}
							<td className="text-end fw-bold text-success">{money(footer.totalTax)}</td>
							<td className="text-end fw-bold">{money(footer.totalAmount)}</td>
						</tr>
					</tbody>
				</Table>
			</div>
		</div>
	);
};

const formatEffectiveTaxPct = (taxAmount, netTaxable) => {
	if (!(netTaxable > 0) || !(taxAmount > 0)) return null;
	return Number(((taxAmount / netTaxable) * 100).toFixed(2));
};

/** Same layout as Clinic Consultation & Services summary rows. */
export const BillingPharmacyTotalsTable = ({
	t,
	totals = {},
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	discountType = "Percentage",
	discountValue = 0,
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	showPosTax = true,
	pharmacyTotal = null,
	showGrandTotal = true,
	grandTotalLabel = null,
}) => {
	const isSameState =
		!patientStateCode || !pharmacyStateCode || pharmacyStateCode === patientStateCode;
	const isGst = String(posTaxType || "").toLowerCase() === "gst";

	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(amount, displayCurrency, getCurrencySymbol);

	const totalTaxable = toDisplay(totals?.subtotal || 0);
	const discount = toDisplay(totals?.discount || 0);
	const netTaxable = toMoneyNumber(totalTaxable - discount);
	const totalCGST = toDisplay(totals?.CGST || 0);
	const totalSGST = toDisplay(totals?.SGST || 0);
	const totalIGST = toDisplay(totals?.IGST || 0);
	const totalVAT = toDisplay(totals?.VAT || 0);
	const totalTax = toDisplay(
		totals?.tax ||
			(isGst
				? isSameState
					? totalCGST + totalSGST
					: totalIGST
				: totalVAT) ||
			0
	);
	const transport = toDisplay(totals?.transport || 0);
	const computedGrand = toMoneyNumber(netTaxable + totalTax + transport);
	const pharmacyGrandTotal = toDisplay(
		pharmacyTotal != null ? pharmacyTotal : totals?.netTotal ?? computedGrand
	);
	const discountLabel = getBillingDiscountSummaryLabel({
		t,
		discountType,
		discountValue,
		discountAmount: discount,
		formatMoney: (amount) => money(amount),
	});
	const showTaxRows = showPosTax && (totalTax > 0 || totalCGST > 0 || totalSGST > 0 || totalIGST > 0 || totalVAT > 0);
	const cgstPct = formatEffectiveTaxPct(totalCGST, netTaxable);
	const sgstPct = formatEffectiveTaxPct(totalSGST, netTaxable);
	const igstPct = formatEffectiveTaxPct(totalIGST, netTaxable);
	const vatPct = formatEffectiveTaxPct(totalVAT > 0 ? totalVAT : totalTax, netTaxable);

	const taxPctLabel = (pct) => (pct != null ? ` (${pct}%)` : "");

	return (
		<>
			<div className="summary-row">
				<span>{t("Total Taxable Value")}</span>
				<strong>{money(totalTaxable)}</strong>
			</div>
			{discount > 0 && (
				<>
					<div className="summary-row">
						<span>{discountLabel}</span>
						<strong className="text-danger">-{money(discount)}</strong>
					</div>
					<div className="summary-row">
						<span>{t("Net Taxable Value")}</span>
						<strong>{formatAmount(netTaxable, displayCurrency, getCurrencySymbol)}</strong>
					</div>
				</>
			)}
			{showTaxRows && isGst && isSameState && totalCGST > 0 && (
				<div className="summary-row">
					<span>
						{t("Add")}: CGST{taxPctLabel(cgstPct)}
					</span>
					<strong className="billing-tax-cell">+{money(totalCGST)}</strong>
				</div>
			)}
			{showTaxRows && isGst && isSameState && totalSGST > 0 && (
				<div className="summary-row">
					<span>
						{t("Add")}: SGST{taxPctLabel(sgstPct)}
					</span>
					<strong className="billing-tax-cell">+{money(totalSGST)}</strong>
				</div>
			)}
			{showTaxRows && isGst && !isSameState && totalIGST > 0 && (
				<div className="summary-row">
					<span>
						{t("Add")}: IGST{taxPctLabel(igstPct)}
					</span>
					<strong className="billing-tax-cell">+{money(totalIGST)}</strong>
				</div>
			)}
			{showTaxRows && !isGst && (totalVAT > 0 || totalTax > 0) && (
				<div className="summary-row">
					<span>
						{t("Add")}: VAT{taxPctLabel(vatPct)}
					</span>
					<strong className="billing-tax-cell">
						+{money(totalVAT > 0 ? totalVAT : totalTax)}
					</strong>
				</div>
			)}
			{showTaxRows && totalTax > 0 && (
				<div className="summary-row billing-pharmacy-tax-total">
					<span>{t("Total Tax")}</span>
					<strong className="billing-tax-cell">{money(totalTax)}</strong>
				</div>
			)}
			{transport > 0 && (
				<div className="summary-row">
					<span>{t("Transport")}</span>
					<strong>+{money(transport)}</strong>
				</div>
			)}
			{showGrandTotal && (
				<div className="summary-row billing-pharmacy-grand-row">
					<span>{grandTotalLabel || t("Pharmacy Grand Total")}</span>
					<strong>{money(pharmacyGrandTotal)}</strong>
				</div>
			)}
		</>
	);
};

export const BillingPharmacySummaryBreakdown = ({
	t,
	cart = [],
	priorPharmacyItems = [],
	itemTaxes = {},
	cartTotals = {},
	totals = {},
	posTaxType = "gst",
	pharmacyStateCode = "",
	patientStateCode = "",
	discountType = "Percentage",
	discountValue = 0,
	displayCurrency,
	pharmacyCurrency,
	convertCurrency,
	getCurrencySymbol,
	isOpen,
	onToggle,
	showPosTax = true,
	pharmacyTotal = null,
}) => {
	const toDisplay = useDisplayAmount(displayCurrency, pharmacyCurrency, convertCurrency);
	const money = (amount) => formatAmount(amount, displayCurrency, getCurrencySymbol);

	const { combinedFooter } = usePharmacyMedicinesDisplayModel({
		cart,
		priorPharmacyItems,
		itemTaxes,
		cartTotals,
		discountType,
		discountValue,
		posTaxType,
		pharmacyStateCode,
		patientStateCode,
		showPosTax,
	});

	const hasPharmacyItems = cart.length > 0 || (priorPharmacyItems?.length || 0) > 0;
	const lineDerivedGrand = toMoneyNumber(
		combinedFooter.taxable - combinedFooter.discount + combinedFooter.tax + (totals?.transport || 0)
	);
	const pharmacyGrandTotal = hasPharmacyItems
		? toDisplay(lineDerivedGrand)
		: pharmacyTotal != null
			? pharmacyTotal
			: toDisplay(totals?.netTotal || 0);

	// Match medicines table Total (taxable before discount) and related footer rows.
	const displayTotals = useMemo(
		() =>
			hasPharmacyItems
				? {
						subtotal: combinedFooter.taxable,
						discount: combinedFooter.discount,
						tax: combinedFooter.tax,
						CGST: combinedFooter.CGST,
						SGST: combinedFooter.SGST,
						IGST: combinedFooter.IGST,
						VAT: combinedFooter.VAT,
						transport: totals?.transport || 0,
						netTotal: lineDerivedGrand,
					}
				: totals,
		[combinedFooter, hasPharmacyItems, lineDerivedGrand, totals]
	);

	return (
		<div className="billing-pharmacy-summary-block mb-3">
			<button
				type="button"
				className="billing-pharmacy-summary-toggle"
				onClick={onToggle}
				aria-expanded={isOpen}
			>
				<span className="fw-semibold">{t("Pharmacy Medicines")}</span>
				<span className="billing-pharmacy-summary-toggle-meta">
					<span className="billing-pharmacy-summary-toggle-total">
						{money(pharmacyGrandTotal)}
					</span>
					<i className={`fa fa-chevron-${isOpen ? "up" : "down"}`} />
				</span>
			</button>

			{isOpen && hasPharmacyItems && (
				<div className="billing-pharmacy-summary-body">
					<BillingPharmacyTotalsTable
						t={t}
						totals={displayTotals}
						posTaxType={posTaxType}
						pharmacyStateCode={pharmacyStateCode}
						patientStateCode={patientStateCode}
						discountType={discountType}
						discountValue={discountValue}
						displayCurrency={displayCurrency}
						pharmacyCurrency={pharmacyCurrency}
						convertCurrency={convertCurrency}
						getCurrencySymbol={getCurrencySymbol}
						showPosTax={showPosTax}
						pharmacyTotal={lineDerivedGrand}
					/>
				</div>
			)}

			{isOpen && !hasPharmacyItems && (
				<div className="billing-pharmacy-summary-body text-muted small">
					{t("No medicines in cart")}
				</div>
			)}
		</div>
	);
};
